import { useCallback, useMemo, useState } from 'react';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { offlineDerInfiniteQueryOptions } from '../api/billing.api';
import { BillingFilterPanel } from '../components/BillingFilterPanel';
import { getDerQueueColumns } from '../components/DerQueueColumns';
import { DerDraftsContext } from '../components/queueDraftsContext';
import {
  BILLING_SEARCH_PLACEHOLDER,
  QUEUE_FIT_CONTENT_COLUMN_IDS,
} from '../constants/billing.constants';
import { useDerBillDownload } from '../hooks/useDerBillDownload';
import { useOfflineDerFilters } from '../hooks/useOfflineDerFilters';
import { getMinCostFilterField, getVerifyStatusFilterField } from '../utils/billingFilterFields';
import { getClaimStatCards, getLimitStatCards } from '../utils/billingStatCards';
import { useVerifyDer } from '../hooks/useVerifyEntry';
import type { OfflineDerRow } from '../types/billing.types';

interface PendingDecision {
  row: OfflineDerRow;
  accept: boolean;
}

export function OfflineDerVerifyPage() {
  const controller = useOfflineDerFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(offlineDerInfiniteQueryOptions(controller.params));
  const limits = firstPageMeta?.limits;
  const verifyDer = useVerifyDer();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [decision, setDecision] = useState<PendingDecision | null>(null);
  const { downloadingDetailId, downloadBill } = useDerBillDownload();

  const handleDraftChange = useCallback((detailId: number, approveAmount: string) => {
    setDrafts((prev) => ({ ...prev, [detailId]: approveAmount }));
  }, []);

  const { search } = controller.params;
  const pendingDetailId = verifyDer.isPending ? (verifyDer.variables?.detailId ?? null) : null;
  const columns = useMemo(() => {
    return getDerQueueColumns({
      mode: 'verify',
      limits,
      onDecide: (row, accept) => setDecision({ row, accept }),
      onDownload: (row) => void downloadBill(row),
      pendingDetailId,
      downloadingDetailId,
      searchText: search,
    });
  }, [limits, downloadBill, pendingDetailId, downloadingDetailId, search]);

  function handleConfirm() {
    if (!decision) return;

    const approveAmount = Number(drafts[decision.row.detailId] ?? decision.row.amount);

    verifyDer.mutate(
      {
        detailId: decision.row.detailId,
        payload: decision.accept ? { accept: true, approveAmount } : { accept: false },
      },
      {
        onSettled: () => {
          setDecision(null);
          setDrafts((prev) => {
            const next = { ...prev };
            delete next[decision.row.detailId];
            return next;
          });
        },
      },
    );
  }

  function describeDecision(): string {
    if (!decision) return '';

    const approved = drafts[decision.row.detailId] ?? decision.row.amount;
    const what = decision.row.expenseType ?? 'expense';

    return decision.accept
      ? `${decision.row.name}'s ${what} will be verified at ${approved} Tk.`
      : `${decision.row.name}'s ${what} will be rejected and keep the claimed ${decision.row.amount} Tk.`;
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Offline DER Verify"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <BillingFilterPanel
          filters={controller}
          fieldsBeforeGeo={[getVerifyStatusFilterField()]}
          fieldsAfterGeo={[getMinCostFilterField()]}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the verification queue"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            stats={[
              ...getClaimStatCards('der', firstPageMeta),
              ...getLimitStatCards('der', limits),
            ]}
          />

          <DerDraftsContext.Provider value={{ drafts, onDraftChange: handleDraftChange }}>
            <DataTable
              data={rows}
              columns={columns}
              isLoading={isLoading}
              className="lg:flex-1"
              stickyColumnId="name"
              fitContentColumnIds={QUEUE_FIT_CONTENT_COLUMN_IDS}
              emptyMessage="Nothing waiting to be verified"
              emptyHint="Try a wider date range or clear the filters."
              {...loadMoreProps}
            />
          </DerDraftsContext.Provider>
        </>
      )}

      <ConfirmDialog
        open={decision !== null}
        onOpenChange={(open) => {
          if (!open) setDecision(null);
        }}
        title={decision?.accept ? 'Verify this expense?' : 'Reject this expense?'}
        description={describeDecision()}
        confirmLabel={decision?.accept ? 'Verify' : 'Reject'}
        confirmVariant={decision?.accept ? 'default' : 'destructive'}
        isPending={verifyDer.isPending}
        pendingLabel="Saving"
        onConfirm={handleConfirm}
      />
    </div>
  );
}
